import { ROUTES } from "@/app/routes";
import { useEffect, useState } from "react";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import StatsCard from "@/app/components/ui/Reusable/StatsCard";
import StatsCardsSkeleton from "@/app/components/ui/Skeletons/StatsCardsSkeleton";
import { IAtHomeBookingStatusSummaryData } from "@/api_framework/api_modals/Patients";
import { IPatientWaitingListState } from "@/app_redux/reducers/slices/auth/patient_waiting_list_slice";

interface IAtHomeStatsProps {
  currentStat: string;
  setStatCard: React.Dispatch<React.SetStateAction<string>>;
}

const normalizeBookingStatus = (status?: string | null) =>
  String(status ?? "")
    .toLowerCase()
    .replace(/[_\s-]+/g, "");

const EMPTY_SUMMARY_STATE: IAtHomeBookingStatusSummaryData = {
  Total: {
    count: 0,
    id: null,
  },
  Attending: {
    count: 0,
    id: null,
  },
  Failed: {
    count: 0,
    id: null,
  },
  Cancelled: {
    count: 0,
    id: null,
  },
  Completed: {
    count: 0,
    id: null,
  },
  "On Hold": {
    count: 0,
    id: null,
  },
  Upcoming: {
    count: 0,
    id: null,
  },
  Waiting: {
    count: 0,
    id: null,
  },
  Confirmed: {
    count: 0,
    id: null,
  },
  "In Transit": {
    count: 0,
    id: null,
  },
};

const getSummaryKeyFromCardLabel = (label: string): keyof IAtHomeBookingStatusSummaryData | null => {
  const key = normalizeBookingStatus(label);
  if (key === "total") return "Total";
  if (key === "upcoming") return "Upcoming";
  if (key === "intransit") return "In Transit";
  if (key === "onhold") return "On Hold";
  if (key === "attending") return "Attending";
  if (key === "waiting") return "Waiting";
  if (key === "completed") return "Completed";
  return null;
};

const AT_HOME_STATS = [
  { label: "Total", value: "Total" },
  { label: "Upcoming", value: "Upcoming" },
  { label: "In Transit", value: "In Transit" },
  { label: "On Hold", value: "On Hold" },
  { label: "Attending", value: "Attending" },
  { label: "Waiting", value: "Waiting" },
  { label: "Completed", value: "Completed" },
] as const;

const AtHomeServicesStats: React.FC<IAtHomeStatsProps> = ({ currentStat, setStatCard }) => {
  const isAtHomeStatsLoading = useLoader("isAtHomeServicesStatsInProgress");
  const { routeState } = useRouteInfo(ROUTES.ROOT.children.PATIENT_WAITING_LIST.id);
  const { atHomeWaitingStats } = routeState.state as IPatientWaitingListState;
  const [hasRequestedStatsAtLeastOnce, setHasRequestedStatsAtLeastOnce] = useState<boolean>(false);
  const hasStatsData = Array.isArray(atHomeWaitingStats) && atHomeWaitingStats.length > 0;
  const statsData = hasStatsData ? atHomeWaitingStats[0] : EMPTY_SUMMARY_STATE;

  useEffect(() => {
    if (isAtHomeStatsLoading || hasStatsData) {
      setHasRequestedStatsAtLeastOnce(true);
    }
  }, [hasStatsData, isAtHomeStatsLoading]);

  const getCountForCard = (label: string) => {
    const summaryKey = getSummaryKeyFromCardLabel(label);
    if (!summaryKey) return 0;
    return statsData[summaryKey]?.count ?? 0;
  };

  const responsiveStatsContainerClassName = "flex w-full gap-3 overflow-x-auto lg:gap-[20px]";
  const statsCardContainerClassName = "min-w-[10rem] flex-none pt-[14px] pb-[10px] items-center lg:min-w-0 lg:flex-1";
  const shouldShowLoader = isAtHomeStatsLoading || (!hasRequestedStatsAtLeastOnce && !hasStatsData);

  if (shouldShowLoader) {
    return <StatsCardsSkeleton />;
  }

  return (
    <div className="w-full lg:py-2">
      <div className={responsiveStatsContainerClassName}>
        {AT_HOME_STATS.map((card) => {
          const isSelected = currentStat?.toLowerCase() === card.value.toLowerCase();
          return (
            <StatsCard
              key={card.value}
              onClick={() => setStatCard(card.value)}
              selected={isSelected}
              unselectedContainerClassName="border-gray-300"
              containerClassName={statsCardContainerClassName}
              value={getCountForCard(card.label)}
              label={card.label}
              valueClassName="text-[1.25rem] font-semibold"
              labelClassName="text-[0.75rem] font-semibold text-center h-[24px] flex justify-center items-start"
            />
          );
        })}
      </div>
    </div>
  );
};

export default AtHomeServicesStats;
